import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit3, CalendarPlus, Trash2, Loader2, X } from 'lucide-react';
import { Loader } from '../../components/Common/Loader';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchSchemeDetails, deleteScheme } from '../../store/schemeSlice';
import { extendScheme } from '../../api/scheme';

export const SchemeDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedSchemeDetails: scheme, loading } = useAppSelector((state) => state.scheme);

  // Extend Modal state
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [extendedDays, setExtendedDays] = useState<number>(15);
  const [remarks, setRemarks] = useState<string>('Extended validity');
  const [submittingExtend, setSubmittingExtend] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchSchemeDetails(id));
    }
  }, [id, dispatch]);

  const handleRemove = async () => {
    if (!scheme) return;
    if (window.confirm(`Are you sure you want to remove scheme "${scheme.name}"?`)) {
      try {
        await dispatch(deleteScheme(scheme.id)).unwrap();
        navigate('/scheme');
      } catch (err: any) {
        alert(err || 'Failed to remove scheme.');
      }
    }
  };

  const handleEdit = () => {
    if (scheme) {
      navigate(`/scheme/edit/${scheme.id}`);
    }
  };

  const handleOpenExtend = () => {
    setExtendedDays(15);
    setRemarks('Extended validity');
    setExtendError(null);
    setIsExtendModalOpen(true);
  };

  const handleSubmitExtend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scheme) return;
    setExtendError(null);

    if (extendedDays <= 0) {
      setExtendError('Please enter a valid positive number of extension days.');
      return;
    }

    try {
      setSubmittingExtend(true);
      await extendScheme(scheme.id, extendedDays, remarks);
      setIsExtendModalOpen(false);
      dispatch(fetchSchemeDetails(scheme.id));
    } catch (err: any) {
      setExtendError(err?.message || 'Failed to extend scheme.');
    } finally {
      setSubmittingExtend(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Loader text="Loading scheme details..." spinnerSize="lg" />
      </div>
    );
  }

  if (!scheme) {
    return (
      <div className="min-h-screen p-6 select-none">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/scheme')}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all text-white flex items-center justify-center cursor-pointer shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">Scheme Not Found</h1>
        </div>
        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm text-center text-gray-500">
          No scheme details available for ID "{id}".
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen select-none pb-12">
      {/* Top Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        {/* Title with Back Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/scheme')}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all text-white flex items-center justify-center cursor-pointer shadow-sm"
            title="Go back to schemes list"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0">
            {scheme.name}
          </h1>
        </div>

        {/* Action Buttons: Edit, Extend, Remove */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={handleEdit}
            className="bg-[#F9C327] hover:bg-[#E5B21E] text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-colors shadow-xs"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit Scheme</span>
          </button>

          <button
            onClick={handleOpenExtend}
            className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-colors shadow-xs"
          >
            <CalendarPlus className="w-4 h-4" />
            <span>Extend Scheme</span>
          </button>

          <button
            onClick={handleRemove}
            className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center gap-2 cursor-pointer transition-colors shadow-xs"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete Scheme</span>
          </button>
        </div>
      </div>

      {/* Main Details Card */}
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm space-y-6">
        {/* Card Header & Status Badge */}
        <div className="flex items-center justify-between border-b border-gray-100 pb-4">
          <h2 className="text-base md:text-lg font-bold text-gray-900 m-0">
            Scheme Specifications
          </h2>
          <span className={`text-[11px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider ${
            scheme.status === 'Active' ? 'bg-emerald-100 text-emerald-600' :
            scheme.status === 'Expired' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
          }`}>
            {scheme.status}
          </span>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-y-6 gap-x-8 text-xs md:text-sm">
          {/* Scheme ID */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Scheme ID</div>
            <div className="font-bold text-[#C1272D] font-mono tracking-tight">{(scheme.code || `${scheme.id}`).replace(/^SCH-/i, '')}</div>
          </div>

          {/* Product SAP Code */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Product SAP Code</div>
            <div className="font-bold text-gray-800 font-mono tracking-tight">{scheme.sapCode || '-'}</div>
          </div>

          {/* Product */}
          <div className="md:col-span-1 lg:col-span-2">
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Product</div>
            <div className="flex items-center gap-2 font-bold text-gray-900">
              <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <span>{scheme.product || '-'}</span>
            </div>
          </div>

          {/* Distributor Channel */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Distributor Channel</div>
            <div className="font-bold text-gray-800">{String(scheme.distType || 'GT MT')}</div>
          </div>

          {/* Zone */}
          {scheme.zone && (
            <div>
              <div className="text-gray-400 font-medium mb-1 text-[11px]">Zone</div>
              <div className="font-bold text-gray-800">{scheme.zone}</div>
            </div>
          )}

          {/* Created by */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Created by</div>
            <div className="font-bold text-gray-800">{scheme.createdBy || '-'}</div>
          </div>

          {/* Created On */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Created On</div>
            <div className="font-bold text-gray-800">{scheme.createdOn || '-'}</div>
          </div>

          {/* Extended by (If available) */}
          {scheme.extendedBy && (
            <div>
              <div className="text-gray-400 font-medium mb-1 text-[11px]">Extended by</div>
              <div className="font-bold text-gray-800">{scheme.extendedBy}</div>
            </div>
          )}

          {/* Extended On (If available) */}
          {scheme.extendedOn && (
            <div>
              <div className="text-gray-400 font-medium mb-1 text-[11px]">
                Extended On <span className="text-gray-500 font-normal">({scheme.extendedDays || ''})</span>
              </div>
              <div className="font-bold text-gray-800">{scheme.extendedOn}</div>
            </div>
          )}

          {/* Duration / Validity */}
          <div>
            <div className="text-gray-400 font-medium mb-1 text-[11px]">Validity / Duration</div>
            <div className="font-bold text-gray-800">{scheme.validity || scheme.duration || '-'}</div>
          </div>
        </div>

        {/* Cities Section */}
        <div className="border-t border-gray-100 pt-6">
          <div className="text-gray-400 font-medium mb-2 text-[11px]">Cities / Target Locations</div>
          <div className="flex flex-wrap gap-2 font-bold text-xs md:text-sm text-gray-800">
            {scheme.citiesList && scheme.citiesList.length > 0 ? (
              scheme.citiesList.map((c, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200/60"
                >
                  {c}
                </span>
              ))
            ) : (
              <span>{scheme.zoneCity || scheme.city || 'All Locations'}</span>
            )}
          </div>
        </div>

        {/* Scheme Mechanics Section */}
        <div className="border-t border-gray-100 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5 text-[#B71C1C]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            <h3 className="font-bold text-[#B71C1C] text-sm md:text-base m-0">
              Scheme Mechanics
            </h3>
          </div>

          <div className="flex flex-wrap items-center gap-8 md:gap-16 pt-2">
            {/* Buy Eligibility */}
            <div>
              <div className="text-xs text-gray-400 font-medium mb-1">Buy Eligibility</div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl md:text-3xl font-extrabold text-gray-900">
                  {scheme.eligibleProducts}
                </span>
                <span className="text-xs md:text-sm font-semibold text-gray-500">
                  {scheme.eligibleUnitType || 'Units'}
                </span>
              </div>
            </div>

            {/* Red Arrow */}
            <div className="text-[#B71C1C] text-xl font-bold">
              →
            </div>

            {/* Get Free */}
            <div>
              <div className="text-xs text-gray-400 font-medium mb-1">Get Free</div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl md:text-3xl font-extrabold text-[#B71C1C]">
                  {scheme.freeProducts}
                </span>
                <span className="text-xs md:text-sm font-semibold text-[#B71C1C]">
                  Product
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Extend Scheme Modal */}
      {isExtendModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center">
                  <CalendarPlus className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-800 tracking-tight m-0">
                    Extend Scheme Validity
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    {scheme.name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsExtendModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {extendError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-xl">
                {extendError}
              </div>
            )}

            <form onSubmit={handleSubmitExtend} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Extension Days*
                </label>
                <div className="flex items-center gap-2 mb-2">
                  {[15, 30, 45, 60].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setExtendedDays(d)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                        extendedDays === d
                          ? 'go_erp-bg text-white border-[#C1272D]'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      +{d} Days
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  required
                  min={1}
                  value={extendedDays}
                  onChange={(e) => setExtendedDays(Number(e.target.value))}
                  placeholder="Enter custom number of days"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Extension Remarks / Reason
                </label>
                <textarea
                  rows={3}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="e.g. Extended for high market demand"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 font-semibold focus:outline-none focus:border-[#C1272D] focus:bg-white transition-all"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsExtendModalOpen(false)}
                  className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingExtend}
                  className="px-5 py-2 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {submittingExtend && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{submittingExtend ? 'Extending...' : 'Confirm Extension'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemeDetails;
